/**
 * Pure-function utilities that determine which Home widgets are visible
 * based on the user's permissions and organization licensing.
 */

import {
  SecurityRolePermission,
  hasModuleViewPermission,
} from 'core/services/permissionService';
import { isProductLicensed } from './homePage';

export interface DashboardWidgetVisibility {
  canViewDashboard: boolean;
  canViewSalesChart: boolean;      // requires Logbook view + Logbook license
  canViewLocalEvents: boolean;     // requires Events license
  canViewHotGames: boolean;        // requires GameMaster view + Game Master license
  canViewChecklistAll: boolean;    // requires Checklist - All view + Checklist license
  canViewChecklistMy: boolean;     // requires Checklist - My view + Checklist license
  canViewTodoAll: boolean;         // requires To-Do - All view + TODO license
  canViewTodoMy: boolean;          // requires To-Do - My view + TODO license
}

/**
 * Derives the full set of manager Home widget visibility flags.
 */
export function resolveDashboardVisibility(
  perms: SecurityRolePermission[],
  products: Array<{ label: string }> = [],
): DashboardWidgetVisibility {
  const licensed = (productLabel: string) => isProductLicensed(products, productLabel);

  return {
    canViewDashboard: hasModuleViewPermission(perms, 'Home'),
    canViewSalesChart:
      licensed('Logbook') && hasModuleViewPermission(perms, 'Logbook'),
    canViewLocalEvents:
      licensed('Events') && hasModuleViewPermission(perms, 'Events'),
    canViewHotGames:
      licensed('Game Master') && hasModuleViewPermission(perms, 'GameMaster'),
    canViewChecklistAll:
      licensed('Checklist') && hasModuleViewPermission(perms, 'Checklist - All'),
    canViewChecklistMy:
      licensed('Checklist') && hasModuleViewPermission(perms, 'Checklist - My'),
    canViewTodoAll:
      licensed('TODO') && hasModuleViewPermission(perms, 'To-Do - All'),
    canViewTodoMy:
      licensed('TODO') && hasModuleViewPermission(perms, 'To-Do - My'),
  };
}

/**
 * Returns the checklist scope that should be used based on permission priority:
 *   'all'  → has Checklist - All view
 *   'my'   → has Checklist - My view only
 *   'none' → no checklist permission
 */
export function resolveChecklistScope(
  perms: SecurityRolePermission[],
): 'all' | 'my' | 'none' {
  if (hasModuleViewPermission(perms, 'Checklist - All')) return 'all';
  if (hasModuleViewPermission(perms, 'Checklist - My')) return 'my';
  return 'none';
}

/**
 * Same pattern for To-Do scope.
 */
export function resolveTodoScope(
  perms: SecurityRolePermission[],
): 'all' | 'my' | 'none' {
  if (hasModuleViewPermission(perms, 'To-Do - All')) return 'all';
  if (hasModuleViewPermission(perms, 'To-Do - My')) return 'my';
  return 'none';
}
